import { FC, ReactNode } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallIntelChip } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { PercentChangeIndicator } from '../percent-change-indicator';

interface ScheduleOpportunityStatsProps {
  scheduledCount: number;
  scheduledRate: number;
  unscheduledCount: number;
  rateChange: number;
}

export const ScheduleOpportunityStats: FC<ScheduleOpportunityStatsProps> = ({
  scheduledCount,
  scheduledRate,
  unscheduledCount,
  rateChange,
}) => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  return (
    <div css={styles.statsContainer}>
      <StatDisplay
        title={scheduledCount}
        subTitle={t('Scheduled')}
        renderIcon={
          <CallIntelChip
            dataKey={CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED}
            label={''}
            chipVariants={{
              [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]: {
                variant: 'success',
                leftIconName: 'check-small',
              },
            }}
          />
        }
      />
      <StatDisplay
        title={unscheduledCount}
        subTitle={t('Unscheduled')}
        renderIcon={
          <CallIntelChip
            dataKey={CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED}
            label={''}
            chipVariants={{
              [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED]: {
                variant: 'warn',
                leftIconName: 'x-small',
              },
            }}
          />
        }
      />
      <StatDisplay
        title={`${scheduledRate}%`}
        subTitle={t('Scheduled Rate')}
        renderTitleSuffix={
          rateChange !== undefined &&
          !!scheduledRate && <PercentChangeIndicator percentChange={rateChange} comparisonPeriod='week' size='medium' />
        }
      />
    </div>
  );
};

interface StatDisplayProps {
  title?: string | number;
  subTitle?: string;
  renderTitleSuffix?: ReactNode;
  rateChange?: number;
  renderIcon?: ReactNode;
}

const StatDisplay: FC<StatDisplayProps> = ({ title, subTitle, renderTitleSuffix, renderIcon }) => {
  return (
    <div>
      <div css={styles.statsTitle}>
        <Text
          as='h3'
          css={{ fontSize: theme.fontSize(48), lineHeight: 1, verticalAlign: 'bottom' }}
          size='large'
          weight='bold'
        >
          {title}
        </Text>
        {renderTitleSuffix}
      </div>
      <div css={styles.statsSubTitleContainer}>
        {renderIcon}
        <Text size='medium' color='subdued'>
          {subTitle}
        </Text>
      </div>
    </div>
  );
};

const styles = {
  statsContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(4)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-wrap: wrap;
      column-gap: ${theme.spacing(4)};
      row-gap: ${theme.spacing(1)};
    }
  `,

  statsTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  statsSubTitleContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
